import format from 'date-fns/format'
import get from 'lodash/get'
import has from 'lodash/has'
import isPlainObject from 'lodash/isPlainObject'
import {createSelector} from 'reselect'

import {HTO_CATEGORIES} from '../const'

import * as eventsSelectors from './events'
import * as userSelectors from './user'

// -- Helpers
const getItemBySlug = (items, slug) => (has(items, slug) ? items[slug] : null)

// Standard
export const categoriesBySlug = (state) => get(state, 'productCategoryPage.categoriesBySlug')
export const getCategory = (state, slug) => getItemBySlug(categoriesBySlug(state), slug)
export const displayedCategory = (state) => get(state, 'productCategoryPage.displayedCategory')

// TODO: Maybe rename displayedCategory to currentCategory and categoryExists
//   to currentCategoryExists, etc.?
export const categoryExists = (state) => isPlainObject(displayedCategory(state))
export const categoryStatus = (state) => get(displayedCategory(state), 'status')
// TODO: Support categoryIsSuccess and categoryIsFailure with one type;
//  not both boolean and object.string
export const categoryIsFailure = (state) => displayedCategory(state) === false
export const categoryIsSuccess = (state) => categoryStatus(state) === 'OK'
export const categoryItems = (state) => get(displayedCategory(state), 'display_items')
export const categoryName = (state) => get(displayedCategory(state), 'name')
export const categorySlug = (state) => get(displayedCategory(state), 'slug')
export const categoryAlt = (state) => get(displayedCategory(state), 'title')
export const categoryImage = (state) => get(displayedCategory(state), 'display_images[0].url')
export const categorySubtitle = (state) => get(displayedCategory(state), 'subtitle')
export const categoryTitle = (state) => get(displayedCategory(state), 'title')
export const categoryPanel = (state) => get(displayedCategory(state), 'panel')

export const currentEventDateFilter = (state) =>
  get(state, 'productCategoryPage.currentEventDateFilter')
export const currentPartyFilter = (state) => get(state, 'productCategoryPage.currentPartyFilter')
export const filteredCategoryItems = (state) =>
  get(state, 'productCategoryPage.filteredCategoryItems')
export const filterModalIsOpen = (state) => get(state, 'productCategoryPage.filterModalIsOpen')
export const isEventFilterDropdownOpen = (state) =>
  get(state, 'productCategoryPage.eventFilterDropdownIsOpen')
export const isEventFilterTouched = (state) =>
  get(state, 'productCategoryPage.eventFilterIsTouched')
export const isFilteredByEventDate = (state) =>
  get(state, 'productCategoryPage.enableEventDateFilter')
export const isFilteredByGroup = (state) => get(state, 'productCategoryPage.enableGroupFilter')
export const isFilteredByHto = (state) => get(state, 'productCategoryPage.enableHtoFilter')
export const isFilteredByIndividual = (state) =>
  get(state, 'productCategoryPage.enableIndividualFilter')
export const isFiltering = (state) => get(state, 'productCategoryPage.isFiltering')
export const isPartyFilterDropdownOpen = (state) =>
  get(state, 'productCategoryPage.partyFilterDropdownIsOpen')
export const isPartyFiltertouched = (state) =>
  get(state, 'productCategoryPage.partyFilterIsTouched')
export const selectedEventDate = (state) => get(state, 'productCategoryPage.selectedEventDate')
export const selectedPartyFilter = (state) => get(state, 'productCategoryPage.selectedPartyFilter')
export const isFilterByBuyActive = (state) => get(state, 'productCategoryPage.isFilterByBuyActive')

// Computed
export const categoryImages = createSelector(categoryImage, (url) =>
  url
    ? {
        phone: `${url}?crop=2000,800&height=400`,
        tablet: `${url}?height=400`,
        smallDesktop: `${url}?height=600`,
        mediumDesktop: `${url}?height=600`,
      }
    : null,
)

/**
 * For an authenticated user, their "default" filter date will be their active event date. So, if
 * the filter date is the same as the active event date, we don't treat this as the date filter being applied.
 */
export const isEventFilterApplied = createSelector(
  isEventFilterTouched,
  selectedEventDate,
  eventsSelectors.currentDate,
  userSelectors.isAuthenticated,
  (eventFilterTouched, filterDate, currentEventDate, isAuthenticated) => {
    const isSelectedDateActiveEventDate = currentEventDate === format(filterDate, 'YYYY-MM-DD')

    return isAuthenticated ? !isSelectedDateActiveEventDate : eventFilterTouched
  },
)

/**
 * For an authenticated user, their "default" filter date will be their active event date. So, if
 * the filter date is the same as the active event date, we don't treat this as the date filter being applied.
 */
export const isFiltersApplied = createSelector(
  isPartyFiltertouched,
  isEventFilterApplied,
  (partyFilterTouched, eventFilterApplied) => {
    return eventFilterApplied || partyFilterTouched
  },
)

export const filterSuccess = createSelector(isFiltering, (filterPending) => !filterPending)

export const htoEnabledCategory = createSelector(categorySlug, (slug) =>
  HTO_CATEGORIES.includes(slug),
)

export const htoFilterSuccess = createSelector(
  filterSuccess,
  isFilteredByHto,
  (filterDone, htoFilterEnabled) => filterDone && htoFilterEnabled,
)

export const isNotShoesOrCufflinks = createSelector(
  categorySlug,
  (slug) => !['cufflinks-studs', 'shoes'].includes(slug),
)

export const isOutfits = createSelector(categorySlug, (slug) => slug === 'outfits')

export const isSuitTuxedo = createSelector(categorySlug, (slug) => slug === 'suits-tuxedos')

export const numberOfProducts = createSelector(filteredCategoryItems, (items) => items.length)
